import React, { useState } from 'react';
import { X, Phone, Calendar } from 'lucide-react';

const CallMarkingForm = ({ student, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    student_id: student.id,
    call_connected: true,
    duration_minutes: 0,
    mentor_notes: '',
    parent_sentiment: 'Neutral',
    student_commitment: 'This Week',
    next_followup_date: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  // Auto-suggest next follow-up date based on commitment
  const getAutoSuggestedDate = (commitment) => {
    const today = new Date();
    let daysToAdd = 7; // default
    
    if (commitment === 'Today') daysToAdd = 1;
    else if (commitment === 'This Week') daysToAdd = 7;
    else if (commitment === 'Need Help') daysToAdd = 3;
    else if (commitment === 'Not Interested') daysToAdd = 14;
    
    const suggestedDate = new Date(today.setDate(today.getDate() + daysToAdd));
    return suggestedDate.toISOString().split('T')[0];
  };

  const handleCommitmentChange = (value) => {
    handleChange('student_commitment', value);
    handleChange('next_followup_date', getAutoSuggestedDate(value));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <Phone className="w-6 h-6 text-green-600" />
            <div>
              <h2 className="text-xl font-bold text-gray-900">Call Follow-up Form</h2>
              <p className="text-sm text-gray-600">{student.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Call Connected */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Call Connected? <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  checked={formData.call_connected === true}
                  onChange={() => handleChange('call_connected', true)}
                  className="mr-2"
                />
                <span>Yes</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  checked={formData.call_connected === false}
                  onChange={() => handleChange('call_connected', false)}
                  className="mr-2"
                />
                <span>No</span>
              </label>
            </div>
          </div>

          {/* Duration */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Call Duration (minutes) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min="0"
              value={formData.duration_minutes}
              onChange={(e) => handleChange('duration_minutes', parseInt(e.target.value))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Parent Sentiment */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Parent Sentiment <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.parent_sentiment}
              onChange={(e) => handleChange('parent_sentiment', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="Positive">😊 Positive</option>
              <option value="Neutral">😐 Neutral</option>
              <option value="Negative">😞 Negative</option>
            </select>
          </div>

          {/* Student Commitment */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Student Commitment <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.student_commitment}
              onChange={(e) => handleCommitmentChange(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="Today">Today (will complete today)</option>
              <option value="This Week">This Week</option>
              <option value="Need Help">Need Help</option>
              <option value="Not Interested">Not Interested</option>
            </select>
          </div>

          {/* Mentor Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Mentor Notes <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.mentor_notes}
              onChange={(e) => handleChange('mentor_notes', e.target.value)}
              rows="4"
              placeholder="Enter detailed notes about the conversation, parent concerns, student issues, action items, etc."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Next Follow-up Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Next Follow-up Date <span className="text-gray-500">(auto-suggested)</span>
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="date"
                value={formData.next_followup_date}
                onChange={(e) => handleChange('next_followup_date', e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <p className="text-xs text-gray-600 mt-1">
              Suggested based on student commitment
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors"
            >
              Save Follow-up
            </button>
          </div>
        </form>

        {/* Info Box */}
        <div className="p-4 bg-blue-50 border-t border-blue-100">
          <p className="text-xs text-blue-800">
            📊 <strong>Auto-saved metrics:</strong> Progress %, Engagement Score, Last Login, Daily Streak
          </p>
          <p className="text-xs text-blue-800 mt-1">
            🎯 <strong>Impact tracking:</strong> System will compare metrics after the follow-up period to measure effectiveness
          </p>
        </div>
      </div>
    </div>
  );
};

export default CallMarkingForm;
